/**
 * useListUrlSync — mirrors a list surface's filter/sort/search state into the
 * `?filters=` / `?sort=` / `?search=` URL params, and reflects external URL
 * changes (browser back/forward, hand-edited deep links) back into state.
 *
 * Ownership doctrine (mirrors useActiveListView exactly):
 * - Component state is authoritative at all times. The URL is a mirror, plus
 *   the mount-time seed (readInitialListUrlState → useListState), plus the
 *   back/forward transport.
 * - This hook is the SINGLE WRITER of its three params. useActiveListView
 *   remains the single writer of `?savedView=`. Foreign params (`?view=`,
 *   `?pipeline=`, `?stage=`, `?slideoutEntityId=`, …) are never touched —
 *   all writes are functional updates over `prev` restricted to our keys.
 * - Writes use `{ replace: true }` so filter/sort/search churn never spams
 *   browser history.
 *
 * Saved-view interplay:
 * - `?filters=` is present ONLY when it carries information: on a saved view,
 *   only while the surface's filters diverge from the view's stored
 *   definition (unsaved overrides); on default/built-in views, only when the
 *   user's filters differ from the surface's defaults.
 * - While the saved-view dirty state is 'unknown' (views list loading, apply
 *   in flight) BOTH `?filters=` and `?sort=` are left untouched — deleting
 *   them early would destroy a deep link's overrides before they can be
 *   classified; writing them would mirror state that may be mid-apply.
 * - The sort baseline is the active saved view's stored sort when one is
 *   active, the surface default otherwise: `?sort=` too only appears when it
 *   differs from what the view/page would show anyway.
 */

import { useEffect, useMemo, useRef } from 'react';
import type { SortDescriptor } from 'react-aria-components';
import { useSearchParams } from 'react-router-dom';

import type { SavedViewDirtyState } from '@/lib/crm/hooks/useSavedViews';
import type { FilterGroup } from '@/types/crm';
import { canonicalizeFilterGroups } from '@/utils/filterUtils';

import {
  decodeFiltersParam,
  decodeSortParam,
  encodeFiltersParam,
  encodeSortParam,
  FILTERS_PARAM,
  SEARCH_PARAM,
  SORT_PARAM,
} from '../urlListParams';

/** Value-equality for sort descriptors (null-safe). */
export function sortDescriptorsEqual(
  a: SortDescriptor | null,
  b: SortDescriptor | null
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return a.column === b.column && a.direction === b.direction;
}

export interface UseListUrlSyncArgs {
  /**
   * The surface's current APPLIED user filters in wire shape. Must be a
   * useMemo-stable value derived from the applied condition groups — never
   * from `filters.filter_groups` (contains forced defaults) and never from
   * `filtersWithSearch` (contains built-in baselines): neither forced
   * defaults nor baselines may ever leak into the URL.
   */
  appliedFilterGroups: FilterGroup[];
  /** The surface's current sort. */
  sortDescriptor: SortDescriptor | null;
  /** Post-debounce search text ('' when empty). */
  searchText: string;

  /**
   * The surface's clean-URL filter baseline (wire shape of the default seed,
   * or [] when the surface has no visible defaults). Param omitted when the
   * current filters canonically equal this. Must be reference-stable.
   */
  defaultFilterGroups: FilterGroup[];
  /** The surface's clean-URL sort baseline (used when no saved view is active). */
  defaultSortDescriptor: SortDescriptor | null;

  /** Active saved-view id from useSavedViews, or null. */
  activeSavedViewId: string | null;
  /** Filter dirty tri-state from useSavedViews. */
  filtersDirtyState: SavedViewDirtyState;
  /**
   * The active saved view's surface-applicable stored filter groups (from
   * useSavedViews), or null while unknown. Applied when the `?filters=` param
   * disappears (back/forward to a clean-view URL).
   */
  activeSavedViewFilterGroups: FilterGroup[] | null;
  /**
   * The active saved view's stored sort (its `view_state.sortDescriptor`), or
   * null. Only consulted while a saved view is active — it is the `?sort=`
   * baseline in that case.
   */
  activeSavedViewSortDescriptor: SortDescriptor | null;
  /**
   * `surfaceTransitionPending` from useSavedViews: true while a change it is
   * making to the surface has not finished committing.
   *
   * The surface's filters and the active-view identity are separate state
   * atoms updated at different priorities, so mid-transition React commits
   * frames where they describe different points in time — e.g. the saved view
   * already cleared while its filters are still applied. Mirroring such a frame
   * writes a param this hook then reads back as an instruction, resurrecting
   * the state the user just left. Treated exactly like an unknown dirty state:
   * both params are left untouched until the transition settles.
   *
   * Optional: surfaces that never drive useSavedViews may omit it.
   */
  surfaceTransitionPending?: boolean;

  /**
   * URL→state appliers. MUST be useCallback-stable and MUST use their
   * argument directly (no stale-closure re-reads).
   */
  onApplyFilterGroups: (groups: FilterGroup[]) => void;
  onApplySort: (descriptor: SortDescriptor | null) => void;
  onApplySearch: (query: string) => void;
}

export function useListUrlSync(args: UseListUrlSyncArgs): void {
  const {
    appliedFilterGroups,
    sortDescriptor,
    searchText,
    defaultFilterGroups,
    defaultSortDescriptor,
    activeSavedViewId,
    filtersDirtyState,
    activeSavedViewFilterGroups,
    activeSavedViewSortDescriptor,
    surfaceTransitionPending = false,
    onApplyFilterGroups,
    onApplySort,
    onApplySearch,
  } = args;

  const [searchParams, setSearchParams] = useSearchParams();

  // Held in refs so the effects can call/read without re-running on every
  // navigation (react-router hands back fresh identities after each write) —
  // the setSearchParamsRef pattern from useActiveListView.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const canonicalApplied = useMemo(
    () => canonicalizeFilterGroups(appliedFilterGroups),
    [appliedFilterGroups]
  );
  const canonicalDefaults = useMemo(
    () => canonicalizeFilterGroups(defaultFilterGroups),
    [defaultFilterGroups]
  );

  // Whether the surface state is settled enough to classify the params.
  //
  // Two independent ways it may not be:
  // - the saved-view dirty state hasn't resolved (views list loading, apply in
  //   flight), so we cannot tell an override from the view's own definition;
  // - a surface transition is mid-commit, so the filters and the active-view
  //   identity may be from different points in time. Mirroring a torn frame
  //   publishes state that was never real and that the URL→state effect then
  //   applies back to the surface.
  //
  // Either way the answer is the same: leave both params exactly as they are
  // until it settles. The effect re-runs when this flips.
  const savedViewStateKnown =
    (activeSavedViewId === null || filtersDirtyState !== 'unknown') &&
    !surfaceTransitionPending;

  // Desired values for our params, refreshed by the state→URL effect and
  // flushed to the URL in a MICROTASK (see flush below). `undefined` = leave
  // the param exactly as it is; `null` = the param must be absent.
  const desiredParamsRef = useRef<{
    filters: string | null | undefined;
    sort: string | null | undefined;
    search: string | null;
  }>({ filters: undefined, sort: undefined, search: null });
  const writePendingRef = useRef(false);
  // Re-armed in the effect BODY (not just initialized) so StrictMode's
  // synthetic unmount/remount cycle — whose cleanup sets it false while the
  // ref initializer never re-runs — cannot permanently kill the flush.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Flush the desired values to the URL. Kept in a per-render ref so the
  // scheduled microtask always runs the LATEST closure.
  //
  // Why a microtask and not a synchronous write from the effect:
  // react-router's functional setSearchParams builds the next params from the
  // RENDER-SCOPED searchParams — not the live location. When another param
  // owner (useActiveListView writing `?savedView=`) navigates in the same
  // effect flush, a synchronous write here would compose on a base that lacks
  // that navigation and silently clobber it (and vice versa). Deferring to a
  // microtask lets the other owner's navigation and its re-render land first,
  // so our setter closure (refreshed via setSearchParamsRef each render)
  // holds the post-write params and the two writes compose. Writes only when
  // a desired value differs from the then-current param.
  const flushDesiredParams = (): void => {
    writePendingRef.current = false;
    if (!isMountedRef.current) {
      return;
    }
    const desired = desiredParamsRef.current;
    const params = searchParamsRef.current;
    const changes: Array<[string, string | null]> = [];
    const consider = (key: string, value: string | null | undefined): void => {
      if (value === undefined) {
        return;
      }
      if ((params.get(key) ?? null) !== value) {
        changes.push([key, value]);
      }
    };
    consider(FILTERS_PARAM, desired.filters);
    consider(SORT_PARAM, desired.sort);
    consider(SEARCH_PARAM, desired.search);
    if (changes.length === 0) {
      return;
    }
    setSearchParamsRef.current(
      prev => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of changes) {
          if (value === null) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        }
        return next;
      },
      { replace: true }
    );
  };
  const flushDesiredParamsRef = useRef(flushDesiredParams);
  flushDesiredParamsRef.current = flushDesiredParams;

  // ---------------------------------------------------------------------------
  // State → URL. Runs on state changes only — NOT on searchParams changes —
  // so an external URL change can never be immediately overwritten by stale
  // state; the URL→state effect below reconciles those.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let desiredFilters: string | null | undefined;
    let desiredSort: string | null | undefined;
    if (!savedViewStateKnown) {
      // Saved-view context unresolved: leave both params untouched — deleting
      // them early would destroy a deep link's overrides before they can be
      // classified.
      desiredFilters = undefined;
      desiredSort = undefined;
    } else if (activeSavedViewId !== null) {
      // A saved view never renders its filters in the URL: `?view=<uuid>`
      // already names them, and inlining a non-trivial definition produces
      // URLs long enough to be unusable. Consequence, accepted deliberately:
      // unsaved filter edits on a saved view are local only and do not survive
      // a reload — the user saves or discards them. A deep link that arrives
      // WITH `?filters=` is still applied (see the URL→state effect); it is
      // simply not re-rendered afterwards.
      desiredFilters = null;
      desiredSort = sortDescriptorsEqual(
        sortDescriptor,
        activeSavedViewSortDescriptor
      )
        ? null
        : encodeSortParam(sortDescriptor);
    } else {
      desiredFilters =
        canonicalApplied === canonicalDefaults
          ? null
          : encodeFiltersParam(appliedFilterGroups);
      desiredSort = sortDescriptorsEqual(sortDescriptor, defaultSortDescriptor)
        ? null
        : encodeSortParam(sortDescriptor);
    }
    const desiredSearch: string | null = searchText !== '' ? searchText : null;

    desiredParamsRef.current = {
      filters: desiredFilters,
      sort: desiredSort,
      search: desiredSearch,
    };
    if (!writePendingRef.current) {
      writePendingRef.current = true;
      queueMicrotask(() => flushDesiredParamsRef.current());
    }
  }, [
    appliedFilterGroups,
    canonicalApplied,
    canonicalDefaults,
    sortDescriptor,
    searchText,
    activeSavedViewId,
    filtersDirtyState,
    savedViewStateKnown,
    activeSavedViewSortDescriptor,
    defaultSortDescriptor,
  ]);

  // Decoded URL values, memoized on the raw param strings so downstream
  // comparisons never churn on reference identity.
  const rawFiltersParam = searchParams.get(FILTERS_PARAM);
  const rawSortParam = searchParams.get(SORT_PARAM);
  const rawSearchParam = searchParams.get(SEARCH_PARAM);
  const decodedUrlFilters = useMemo(
    () => decodeFiltersParam(rawFiltersParam),
    [rawFiltersParam]
  );
  const decodedUrlSort = useMemo(
    () => decodeSortParam(rawSortParam),
    [rawSortParam]
  );

  // Latest state values readable by the URL→state effect without being deps —
  // the effect must fire on URL changes, not on state changes (the state→URL
  // effect covers those).
  const stateRef = useRef({
    canonicalApplied,
    sortDescriptor,
    searchText,
    activeSavedViewFilterGroups,
    activeSavedViewSortDescriptor,
    activeSavedViewId,
    savedViewStateKnown,
    defaultFilterGroups,
    canonicalDefaults,
    defaultSortDescriptor,
    onApplyFilterGroups,
    onApplySort,
    onApplySearch,
  });
  stateRef.current = {
    canonicalApplied,
    sortDescriptor,
    searchText,
    activeSavedViewFilterGroups,
    activeSavedViewSortDescriptor,
    activeSavedViewId,
    savedViewStateKnown,
    defaultFilterGroups,
    canonicalDefaults,
    defaultSortDescriptor,
    onApplyFilterGroups,
    onApplySort,
    onApplySearch,
  };

  // ---------------------------------------------------------------------------
  // URL → state. Reflects external URL changes (back/forward, deep-link
  // edits) into the surface. Guarded by value equality so it only applies on
  // genuine difference — preventing an update loop with the state→URL effect.
  // A malformed param decodes to null and is treated as absent (fail closed);
  // the state→URL effect subsequently rewrites or removes it.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const s = stateRef.current;

    // Filters.
    if (decodedUrlFilters !== null) {
      if (canonicalizeFilterGroups(decodedUrlFilters) !== s.canonicalApplied) {
        s.onApplyFilterGroups(decodedUrlFilters);
      }
    } else if (s.activeSavedViewId !== null) {
      // Param absent on a saved view is the NORMAL state — a saved view does
      // not render its filters in the URL — so it says nothing about the
      // surface and must not touch it. applyView owns landing the view's stored
      // filters; local edits stay local until saved or discarded.
      //
      // Restoring here would be actively wrong: this effect also fires when the
      // sort or search param changes, so an unrelated sort tweak would silently
      // revert the user's in-progress filter edits.
    } else if (s.canonicalApplied !== s.canonicalDefaults) {
      s.onApplyFilterGroups(s.defaultFilterGroups);
    }

    // Sort. An absent param means the baseline sort (the saved view's stored
    // sort when one is active — left alone while unknown — else the default).
    let baselineSort: SortDescriptor | null | undefined;
    if (s.activeSavedViewId === null) {
      baselineSort = s.defaultSortDescriptor;
    } else if (s.savedViewStateKnown) {
      baselineSort = s.activeSavedViewSortDescriptor;
    } else {
      baselineSort = undefined;
    }
    const desiredSortState =
      decodedUrlSort !== null ? decodedUrlSort : baselineSort;
    if (
      desiredSortState !== undefined &&
      !sortDescriptorsEqual(desiredSortState, s.sortDescriptor)
    ) {
      s.onApplySort(desiredSortState);
    }

    // Search.
    const urlSearch = rawSearchParam ?? '';
    if (urlSearch !== s.searchText) {
      s.onApplySearch(urlSearch);
    }
    // stateRef holds the latest state/callbacks; only URL changes drive this
    // effect (see comment above).
  }, [decodedUrlFilters, decodedUrlSort, rawSearchParam]);
}
